<template>
  <tr class="table-row" :class="classes">
    <td class="text-center" style="width:60px">
      <strong>{{ data.startDate | date('HH:mm') }}</strong>
    </td>
    <td class="text-center" style="width:45px">
      <dx-dropdown :items="statuses" label="name"
                   class="input-group-addon appointment-status"
                   @select="setStatus"
                   :disabled="!canChangeStatus"
                   :max-height="350">
        <span class="square c-hand" :title="statusName"
            :style="{backgroundColor: statusColor}"></span>
        <template v-slot:item="{ item }">
          <div class="tile tile-centered c-hand">
            <div class="tile-icon">
              <span class="square" :style="{backgroundColor: item.color}"></span>
            </div>
            <div class="tile-content">{{ item.name }}</div>
          </div>
        </template>
      </dx-dropdown>
      <div v-if="data.status === 'waiting' && data.arrivedAt">
        <small :class="setStatusColor()">
          {{ $timeDiff(data.arrivedAt, now, 'HH:mm') }}
        </small>
      </div>
    </td>
    <td
      class="text-ellipsis"
      :class="data.status !== 'blocked' ? 'c-hand' : ''"
      style="width: 30%;"
      @click="openAppointment(data)"
    >
      <template v-if="data.patient">
        <div>
          <span>{{ data.patient.name }}</span>
          <span v-if="!data.patient.id"> (não cadastrado)</span><br>
          <small>{{ (data.patient.cellphone || data.patient.phone) | phone }}</small>
        </div>
        <small v-if="data.notes" class="text-warning">{{ data.notes }}</small>
      </template>
      <template v-else>
        <span class="text-gray">{{ label }}</span><br>
        <small class="text-info">{{ data.notes }}</small>
      </template>
    </td>
    <td class="text-ellipsis">
      <template v-if="data.insurance">
        <span>{{ data.insurance.name }}</span><br>
      </template>
      <template v-else>
        <span v-if="insuranceNames.length">
          {{ insuranceNames[0] }}
          <span
            v-if="insuranceNames.length > 1"
            class="label text-primary tooltip hover-link ml-1 "
            :data-tooltip="insuranceNames.slice(1).join('\n')">
            +{{ insuranceNames.length -1 }}
          </span>
        </span>
      </template>
      <small v-if="expenseNames.length">
        <span class="text-info">{{ expenseNames[0] }}</span>
        <span
          v-if="expenseNames.length > 1"
          class="label text-primary tooltip hover-link ml-1 "
          :data-tooltip="expenseNames.slice(1).join('\n')">
          +{{ expenseNames.length -1 }}
        </span>
      </small>
    </td>
    <td class="text-ellipsis">
      <span>{{ scheduleName }}</span><br>
      <small v-if="professionalNames.length">
        {{ professionalNames[0] }}
        <span
          v-if="professionalNames.length > 1"
          class="tooltip hover-link"
          :data-tooltip="professionalNames.slice(1).join('\n')">
          (+{{ professionalNames.length -1 }})
        </span>
      </small>
    </td>
    <td class="text-center" style="width:45px">
      <dx-dropdown :items="menuAppointmentOptions" label="name"
                   :disabled="menuAppointmentOptions.length === 0"
                   direction="right" class="appointment-actions" v-if="!waiting">
        <button class="btn btn-icon btn-action btn-link text-dark">
          <fa-icon :icon="['fal', 'bars']"/>
        </button>
        <template v-slot:item="{ item }">
          <div @click="menuAction(item, data)">
            <fa-icon class="ml-1" :icon="item.icon"/>
            <span class="ml-2">{{ item.name }}</span>
          </div>
        </template>
      </dx-dropdown>
      <div class="loading" v-else></div>
    </td>
  </tr>
</template>

<script>
import moment from 'moment';
import {
  mapMutations,
  mapGetters,
  mapState,
} from 'vuex';
import appointmentStatuses,
{
  getColor,
  getName,
  STATUS_AVAILABLE,
  STATUS_BLOCKED,
} from 'src/data/appointment-statuses';
import {
  create as createAppointment,
  update as updateAppointment,
  remove as removeAppointment,
} from 'src/app/appointment/api';
import { APPOINTMENT_NO_SCHEDULES } from '@/data/contants';
import * as council from 'src/data/council-types';
import * as menuOptions from 'src/data/appointment-menu-options';
import * as types from 'src/data/appointment-types';

export default {
  props: {
    data: {},
  },
  data() {
    return {
      council,
      menuOptions,
      statuses: appointmentStatuses,
      types,
      saving: false,
      locking: false,
      unlocking: false,
      deleting: false,
    };
  },
  computed: {
    ...mapGetters({
      getProfessionalById: 'surgeryGetProfessionalById',
      getScheduleById: 'surgeryGetScheduleById',
      getInsuranceByPlanId: 'surgeryGetInsuranceByPlanId',
    }),
    ...mapState({
      now: ({ now }) => now,
      clipboard: ({ surgeryAppointment }) => surgeryAppointment.clipboard,
      user: state => state.auth.user,
    }),
    classes() {
      const items = [];
      if (this.data.status === STATUS_BLOCKED) {
        items.push('appointment-block');
      }
      return items;
    },
    label() {
      if (this.data.status === STATUS_BLOCKED) {
        return this.data.refKey ? 'Horário reservado' : 'Horário bloqueado';
      }
      return 'Horário disponível';
    },
    schedule() {
      return this.getScheduleById(this.data.scheduleId);
    },
    scheduleName() {
      return this.schedule ? this.schedule.name : APPOINTMENT_NO_SCHEDULES;
    },
    professionals() {
      return this.data.available.professionals
        .map(({ id }) => this.getProfessionalById(id));
    },
    professionalNames() {
      return this.professionals.map(({ name }) => name);
    },
    insurances() {
      return this.data.available.insurancePlans
        .map(id => this.getInsuranceByPlanId(id));
    },
    insuranceNames() {
      return this.insurances
        .map(({ customName }) => customName)
        .sort();
    },
    expenseNames() {
      return this.data.expenses
        .map(expense => `${expense.code} - ${expense.name}`)
        .sort();
    },
    menuAppointmentOptions() {
      const options = this.menuOptions.options
        .filter(option => (option === '-' ? [] : option.verify(this.data, this.clipboard)));
      if (options.length > 0 && options[0] === '-') {
        options.shift();
      }
      return options;
    },
    statusColor() {
      return getColor(this.data.status);
    },
    statusName() {
      return getName(this.data.status);
    },
    waiting() {
      return this.saving || this.locking || this.unlocking || this.deleting;
    },
    hasAccount() {
      return this.data.expenses.some(item => !!item.accountId);
    },
    canChangeStatus() {
      return ![STATUS_BLOCKED, STATUS_AVAILABLE].includes(this.data.status);
    },
    canOpenAppointment() {
      return ![STATUS_BLOCKED].includes(this.data.status);
    },
  },
  methods: {
    ...mapMutations({
      showAppointmentModal: 'SurgeryAppointment.SHOW_APPOINTMENT_MODAL',
      upsertLine: 'SurgeryAppointment.UPSERT',
      clearLine: 'SurgeryAppointment.REMOVE',
      copy: 'SurgeryAppointment.COPY_APPOINTMENT',
      cut: 'SurgeryAppointment.CUT_APPOINTMENT',
      clearClipboard: 'SurgeryAppointment.CLEAR_CLIPBOARD',
    }),
    openAppointment(data) {
      if (!this.canOpenAppointment) {
        return;
      }
      this.showAppointmentModal(data);
    },
    setStatus({ key }) {
      if ((key === 'waiting'
        || key === 'finished'
        || key === 'in_attendance'
        || key === 'screening'
        || key === 'report')
        && this.user.branch.settings
        && this.user.branch.settings['clinical.blockServiceWithoutAccount']) {
        const hasAccount = !!this.data.expenses.find(({ accountId }) => accountId);
        if (!hasAccount) {
          this.$toast.show('É obrigatório finalizar uma conta', { type: 'error' });
          return;
        }
      }

      const params = {
        status: key,
      };

      if (key === 'waiting') {
        params.arrivedAt = moment().format('YYYY-MM-DDTHH:mm');
        this.data.arrivedAt = params.arrivedAt;
      } else if (!['in_attendance', 'finished', 'report', 'payment', 'screening'].includes(key)) {
        params.arrivedAt = null;
        this.data.arrivedAt = null;
      }

      this.updateAppointment(this.data.id, params)
        .catch(() => {});
      this.data.status = key;
    },
    setStatusColor() {
      const timeDiff = this.$timeDiff(this.data.arrivedAt, this.now, 'HH:mm');
      if (timeDiff > '00:09' && timeDiff < '00:20') {
        return 'text-error';
      }
      if (timeDiff > '00:20') {
        return 'text-error text-bold';
      }
      return 'text-dark';
    },
    menuAction(menu, data) {
      switch (menu.type) {
        case 'new':
        case 'edit': {
          this.showAppointmentModal(data);
          break;
        }
        case 'copy': {
          this.copy(data);
          break;
        }
        case 'cut': {
          this.menuCutAppointment(data);
          break;
        }
        case 'paste': {
          this.menuPasteAppointment(data);
          break;
        }
        case 'lock': {
          this.menuLockAppointment();
          break;
        }
        case 'unlock': {
          this.menuUnlockAppointment();
          break;
        }
        case 'delete': {
          this.menuDeleteAppointment();
          break;
        }
        default: {
          break;
        }
      }
    },
    menuCutAppointment(data) {
      if (this.hasAccount) {
        this.$toast.show('Agendamento vinculado a uma fatura', {
          type: 'error',
          timeout: 5000,
        });
        return;
      }
      this.cut(data);
    },
    menuPasteAppointment(data) {
      const {
        data: clipboardData,
        event: clipboardEvent,
      } = this.clipboard;

      if (moment(data.startDate).isBefore(moment(), 'day')) {
        this.$toast.show('Não é possível copiar o agendamento para uma data anterior à hoje.', {
          type: 'error',
          timeout: 10000,
        });
        return;
      }

      const destinationDateTime = moment(data.startDate);
      const clipboardDateTime = moment(clipboardData.startDate);

      if (destinationDateTime.isBefore(clipboardDateTime, 'day')) {
        this.$toast.show('Não é permitido colar agendamento em data anterior ao original', {
          type: 'error',
          timeout: 5000,
        });
        return;
      }

      const originalInsurancePlanId = clipboardData.insurance.plan.id;
      const availableInsurancePlans = data.available.insurancePlans || [];

      if (!availableInsurancePlans.includes(originalInsurancePlanId)) {
        this.$toast.show('O horário selecionado não atende o convênio original deste agendamento', {
          type: 'error',
          timeout: 5000,
        });
        return;
      }

      const professional = data.available.professionals
        .find(({ id }) => id === clipboardData.professional.id)
        || data.available.professionals[0];

      const professionalData = {
        id: professional.id,
        specialty: undefined,
      };

      if (professional.specialties.length > 0) {
        if (clipboardData.professional.specialty) {
          professionalData.specialty = professional.specialties
            .find(specialty => specialty === clipboardData.professional.specialty);
        }

        if (!professionalData.specialty) {
          [professionalData.specialty] = professional.specialties;
        }
      }

      const request = clipboardEvent === 'cut'
        ? this.updateAppointment(clipboardData.id, {
          scheduleId: data.scheduleId,
          startDate: data.startDate,
          endDate: data.endDate,
          professional: professionalData,
          originalKey: clipboardData.key,
          status: 'scheduled',
        })
        : this.createAppointment({
          scheduleId: data.scheduleId,
          startDate: data.startDate,
          endDate: data.endDate,
          professional: professionalData,
          patient: clipboardData.patient,
          insurance: {
            id: clipboardData.insurance.id,
            record: clipboardData.insurance.record,
            validity: clipboardData.insurance.validity,
            planId: clipboardData.insurance.plan.id,
          },
          type: data.type || clipboardData.type,
          status: 'scheduled',
          expenses: clipboardData.expenses,
          notes: clipboardData.notes,
        });

      request
        .then((appointmentData) => {
          this.upsertLine(appointmentData);
          this.clearClipboard();
        })
        .catch(() => {});
    },
    async menuDeleteAppointment() {
      if (this.hasAccount) {
        this.$toast.show('Agendamento vinculado a uma fatura', {
          type: 'error',
          timeout: 5000,
        });
        return;
      }

      this.$dialog.show('', {
        html:
          '<div class="text-center">'
          + '<h5 class="text-center">Atenção!</h5>'
          + '<div>Deseja realmente excluir este agendamento?</div>'
          + '</div>',
        buttons: [
          {
            label: 'Não',
            classes: '',
          }, {
            label: 'Sim',
            classes: 'btn-primary btn-error ml-2',
            click: close => (
              this.deleteAppointment(this.data.id)
                .then(() => this.clearLine(this.data))
                .catch(() => {})
                .finally(() => close())
            ),
          },
        ],
      });
    },
    menuLockAppointment() {
      if (this.locking) {
        return;
      }

      let allow = true;
      if (this.user.branch
        && this.user.branch.customerId === '5ef4ade28b64ba627e469aab'
        && this.user.branch.roles
        && this.user.branch.roles.length > 0) {
        this.user.branch.roles.forEach(({ actions }) => {
          const found = actions.find(({ action }) => action === 'professional.*');
          if (found) {
            allow = false;
          }
        });
      }

      if (!allow) {
        this.$toast.show('Permissão negada!', { type: 'error' });
        return;
      }

      this.locking = true;

      const formData = {
        scheduleId: this.data.scheduleId,
        startDate: this.data.startDate,
        endDate: this.data.endDate,
        professional: this.data.available.professionals[0],
        type: this.data.type,
        status: STATUS_BLOCKED,
      };
      this.createAppointment(formData)
        .then((data) => {
          this.data.id = data.id;
          this.data.status = STATUS_BLOCKED;
        })
        .catch(() => {})
        .finally(() => {
          this.locking = false;
        });
    },
    menuUnlockAppointment() {
      if (this.unlocking) {
        return;
      }

      let allow = true;
      if (this.user.branch
        && this.user.branch.customerId === '5ef4ade28b64ba627e469aab'
        && this.user.branch.roles
        && this.user.branch.roles.length > 0) {
        this.user.branch.roles.forEach(({ actions }) => {
          const found = actions.find(({ action }) => action === 'professional.*');
          if (found) {
            allow = false;
          }
        });
      }

      if (!allow) {
        this.$toast.show('Permissão negada!', { type: 'error' });
        return;
      }

      this.unlocking = true;

      this.deleteAppointment(this.data.id)
        .then(() => this.clearLine(this.data))
        .catch(() => {})
        .finally(() => {
          this.unlocking = false;
        });
    },
    async createAppointment(data) {
      this.saving = true;
      try {
        return await createAppointment(data);
      } catch (e) {
        this.$toast.error(e);
        throw e;
      } finally {
        this.saving = false;
      }
    },
    async updateAppointment(id, data) {
      this.saving = true;
      try {
        return await updateAppointment(id, data);
      } catch (e) {
        this.$toast.error(e);
        throw e;
      } finally {
        this.saving = false;
      }
    },
    async deleteAppointment(id) {
      this.deleting = true;
      try {
        return await removeAppointment(id);
      } catch (e) {
        this.$toast.error(e);
        throw e;
      } finally {
        this.deleting = false;
      }
    },
  },
};
</script>

<style lang="scss">
@import 'src/assets/scss/variables';
.table-row {
  .appointment-status {
    &.dropdown > a {
      display: block;
      margin-top: .05rem;
      &:focus {
        box-shadow: none;
      }
    }
    .tile-content {
      line-height: $line-height;
      text-align: left;
      white-space: nowrap;
    }
  }
  .appointment-actions {
    margin-top: .2rem;
    color: $dark-color;
    .menu {
      a {
        display: flex;
        .square {
          margin-right: $layout-spacing;
        }
      }
    }
    .menu-item {
      padding: $layout-spacing-sm;
      text-align: left;
      .divider {
        margin: 0;
      }
      &:hover {
        background-color: $secondary-color;
        border-radius: $border-radius;
        color: $light-color;
        cursor: pointer;
      }
    }
  }
}
.appointment-block {
  background-color: #f0f1f4;
  color: darkgray;
}
</style>
